/**
 * Validates a tool package JSON object for structure and safety.
 * Returns an array of error strings (empty array = valid).
 */
export function validatePackageJson(json: any): string[] {
  const errors: string[] = []

  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    errors.push('File must be a JSON object')
    return errors
  }

  const allowedTopKeys = new Set(['package', 'tools'])
  for (const key of Object.keys(json)) {
    if (!allowedTopKeys.has(key)) {
      errors.push(`Unexpected top-level key: "${key}"`)
    }
  }

  if (!json.package || typeof json.package !== 'object') {
    errors.push('Missing "package" metadata object')
  } else {
    if (!json.package.name || typeof json.package.name !== 'string') {
      errors.push('Package "name" is required and must be a string')
    } else if (json.package.name.length > 255) {
      errors.push('Package name exceeds 255 characters')
    }

    if (json.package.connectionTypeId == null || typeof json.package.connectionTypeId !== 'number') {
      errors.push('Package "connectionTypeId" is required and must be a number')
    }

    if (json.package.version && typeof json.package.version !== 'string') {
      errors.push('Package "version" must be a string')
    }

    if (json.package.description && typeof json.package.description !== 'string') {
      errors.push('Package "description" must be a string')
    } else if (json.package.description && json.package.description.length > 1000) {
      errors.push('Package description exceeds 1000 characters')
    }
  }

  if (!Array.isArray(json.tools)) {
    errors.push('Missing "tools" array')
  } else if (json.tools.length === 0) {
    errors.push('Package must contain at least one tool')
  } else {
    const allowedToolKeys = new Set([
      'name', 'title', 'description', 'inputSchema', 'outputSchema', 'annotations',
      'httpMethod', 'path', 'queryMap', 'headerMap', 'formEnabled',
      'bodyTemplate', 'responseTemplate', 'version', 'sampleResponse', 'tags',
    ])
    const validMethods = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'SELECT'])
    const seenNames = new Set<string>()

    for (let i = 0; i < json.tools.length; i++) {
      const tool = json.tools[i]
      const prefix = `Tool[${i}]`

      if (!tool || typeof tool !== 'object' || Array.isArray(tool)) {
        errors.push(`${prefix}: must be an object`)
        continue
      }

      for (const key of Object.keys(tool)) {
        if (!allowedToolKeys.has(key)) {
          errors.push(`${prefix}: unexpected key "${key}"`)
        }
      }

      if (!tool.name || typeof tool.name !== 'string') {
        errors.push(`${prefix}: "name" is required and must be a string`)
      } else {
        if (tool.name.length > 255) errors.push(`${prefix}: name exceeds 255 characters`)
        if (seenNames.has(tool.name)) errors.push(`${prefix}: duplicate tool name "${tool.name}"`)
        seenNames.add(tool.name)
      }

      if (tool.title && typeof tool.title !== 'string') {
        errors.push(`${prefix}: "title" must be a string`)
      } else if (tool.title && tool.title.length > 255) {
        errors.push(`${prefix}: title exceeds 255 characters`)
      }

      if (!tool.description || typeof tool.description !== 'string') {
        errors.push(`${prefix}: "description" is required and must be a string`)
      } else if (tool.description.length > 1000) {
        errors.push(`${prefix}: description exceeds 1000 characters`)
      }

      if (tool.httpMethod && !validMethods.has(tool.httpMethod.toUpperCase())) {
        errors.push(`${prefix}: invalid httpMethod "${tool.httpMethod}"`)
      }

      if (tool.formEnabled != null && typeof tool.formEnabled !== 'boolean') {
        errors.push(`${prefix}: "formEnabled" must be a boolean`)
      }

      if (tool.inputSchema && typeof tool.inputSchema !== 'object') {
        errors.push(`${prefix}: "inputSchema" must be an object`)
      }

      if (tool.bodyTemplate && typeof tool.bodyTemplate !== 'string') {
        errors.push(`${prefix}: "bodyTemplate" must be a string`)
      } else if (tool.bodyTemplate && tool.bodyTemplate.length > 10000) {
        errors.push(`${prefix}: bodyTemplate exceeds 10000 characters`)
      }

      if (tool.responseTemplate && typeof tool.responseTemplate !== 'string') {
        errors.push(`${prefix}: "responseTemplate" must be a string`)
      } else if (tool.responseTemplate && tool.responseTemplate.length > 10000) {
        errors.push(`${prefix}: responseTemplate exceeds 10000 characters`)
      }

      if (errors.length >= 5) {
        errors.push('... additional errors omitted')
        return errors
      }
    }
  }

  return errors
}
